import { useState, useEffect, useCallback } from 'react';
import { Conversation, Message, ConversationsState } from '../types/conversations';
import { generateMockConversations, generateConversationTitle } from '../utils/conversations';
import { useIsMobile } from './useMediaQuery';

export function useConversations() {
  const isMobile = useIsMobile();
  const [state, setState] = useState<ConversationsState>({
    conversations: [],
    activeConversationId: null,
    isLoading: true,
    isMobile
  });

  // Update mobile state when it changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      isMobile
    }));
  }, [isMobile]);

  // Initialize conversations
  useEffect(() => {
    const conversations = generateMockConversations();
    setState(prev => ({
      ...prev,
      conversations,
      isLoading: false
    }));
  }, []);

  const selectConversation = useCallback((conversationId: string) => {
    setState(prev => ({
      ...prev,
      activeConversationId: conversationId,
      conversations: prev.conversations.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    }));
  }, []);

  const createNewConversation = useCallback(() => {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: 'New Conversation',
      lastMessage: '',
      lastMessageTime: new Date(),
      messages: []
    };

    setState(prev => ({
      ...prev,
      conversations: [newConversation, ...prev.conversations],
      activeConversationId: newConversation.id
    }));

    return newConversation.id;
  }, []);

  const addMessageToConversation = useCallback(async (
    conversationId: string, 
    content: string, 
    role: 'user' | 'assistant' = 'user'
  ): Promise<void> => {
    const newMessage: Message = {
      id: Date.now().toString() + Math.random(),
      role,
      content,
      timestamp: new Date()
    };

    setState(prev => {
      const updatedConversations = prev.conversations.map(conv => {
        if (conv.id !== conversationId) return conv;

        const updatedMessages = [...conv.messages, newMessage];
        const newTitle = conv.title === 'New Conversation' && updatedMessages.length > 0
          ? generateConversationTitle(updatedMessages)
          : conv.title;

        return {
          ...conv,
          title: newTitle,
          messages: updatedMessages,
          lastMessage: content,
          lastMessageTime: new Date()
        };
      });

      // Sort conversations by last message time
      updatedConversations.sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());

      return {
        ...prev,
        conversations: updatedConversations
      };
    });
  }, []);

  const sendMessage = useCallback(async (conversationId: string, content: string): Promise<void> => {
    // Add user message immediately
    await addMessageToConversation(conversationId, content, 'user');

    // Simulate AI response
    try {
      const conversation = state.conversations.find(c => c.id === conversationId);
      if (!conversation) return;

      const allMessages = [...conversation.messages, {
        id: 'temp',
        role: 'user' as const,
        content,
        timestamp: new Date()
      }];

      const response = await fetch(/* 'http://localhost:8000/chats/stream' */ 'https://smorgbot-poc-api.smorg.io/chats/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // messages: allMessages.map(m => ({ role: m.role, content: m.content }))
          message: allMessages.map(m => m.content).join('\n'),
          user_id: "112",
          thread_id: "111",
          artefact_id: "111"
        })
      });

      if (response.ok && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        // Add placeholder assistant message
        const assistantMessageId = Date.now().toString() + '_assistant';
        const assistantMessage: Message = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date()
        };

        setState(prev => ({
          ...prev,
          conversations: prev.conversations.map(conv => 
            conv.id === conversationId 
              ? { 
                  ...conv, 
                  messages: [...conv.messages, assistantMessage],
                  lastMessage: '',
                  lastMessageTime: new Date()
                }
              : conv
          )
        }));

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.trim() && line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.content || data.delta?.content) {
                    const newContent = data.content || data.delta.content;
                    
                    setState(prev => ({
                      ...prev,
                      conversations: prev.conversations.map(conv => 
                        conv.id === conversationId 
                          ? {
                              ...conv,
                              messages: conv.messages.map(msg => 
                                msg.id === assistantMessageId 
                                  ? { ...msg, content: msg.content + newContent }
                                  : msg
                              ),
                              lastMessage: conv.messages.find(m => m.id === assistantMessageId)?.content + newContent || newContent,
                              lastMessageTime: new Date()
                            }
                          : conv
                      )
                    }));
                  }
                } catch (e) {
                  // Ignore parsing errors for individual chunks
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      await addMessageToConversation(conversationId, 'Sorry, I encountered an error. Please try again.', 'assistant');
    }
  }, [state.conversations, addMessageToConversation]);

  const deleteConversation = useCallback((conversationId: string) => {
    setState(prev => {
      const filteredConversations = prev.conversations.filter(c => c.id !== conversationId);
      const newActiveId = prev.activeConversationId === conversationId 
        ? (filteredConversations[0]?.id || null)
        : prev.activeConversationId;

      return {
        ...prev,
        conversations: filteredConversations,
        activeConversationId: newActiveId
      };
    });
  }, []);

  const clearConversations = useCallback(() => {
    setState(prev => ({
      ...prev,
      conversations: [],
      activeConversationId: null
    }));
  }, []);

  const getActiveConversation = useCallback(() => {
    return state.conversations.find(c => c.id === state.activeConversationId) || null;
  }, [state.conversations, state.activeConversationId]);

  return {
    ...state,
    selectConversation,
    createNewConversation,
    addMessageToConversation,
    sendMessage,
    deleteConversation,
    clearConversations,
    getActiveConversation
  };
}